import { ORDER_TEST_ITEM_FIELDS } from "@/graphql/shop";
import { gql } from "@apollo/client";

export const USER_ENTITLEMENTS_QUERY = gql`
  query UserEntitlementsQuery {
    user {
      id
      entitlements {
        key
        granted
      }
    }
  }
`;

export const REPORT_FIELDS = gql`
  fragment ReportFields on ReportType {
    id
    name
    slug
    isAvailable
    questionsAndAnswersCount
    reportInformationPanels {
      id
      title
      body
      diagram
    }
    purchaseProduct {
      id
      name
      status
      content {
        description
        shortDescription
        reportIcon
        informationPanels {
          id
          title
          body
          displayContext
        }
      }
      functionalDatapointCount
      dnaDatapointCount
      grantedEntitlements {
        key
      }
      paymentIsoCode
      organisation
    }
  }
`;

export const USER_REPORT_FIELDS = gql`
  fragment UserReportFields on UserReportType {
    id
    name
    slug
    publicationStatus
    publishedViews
  }
`;

export const REPORTS_LIST_QUERY = gql`
  query ReportsListQuery {
    reports {
      ...ReportFields
      functionalDatapointCount
      dnaDatapointCount
    }
    userReports {
      id
      subsector {
        id
        name
        slug
        purchaseProduct {
          id
          paymentIsoCode
          organisation
          content {
            reportIcon
          }
          grantedEntitlements {
            key
          }
        }
      }
      functionalDatapointCounts {
        inRangeCount
        outOfRangeCount
        untestedCount
      }
      dnaDatapointCounts {
        inRangeCount
        outOfRangeCount
        untestedCount
      }
      questionsAndAnswersCounts {
        inRangeCount
        outOfRangeCount
        untestedCount
      }
    }
  }
  ${REPORT_FIELDS}
`;

export const CUSTOMER_RELATED_REPORTS_LIST_QUERY = gql`
  query CustomerRelatedReportsListQuery($userTestId: ID!) {
    reports(userTestId: $userTestId) {
      ...ReportFields
      functionalDatapointCount
      dnaDatapointCount
    }
    userReports {
      id
      subsector {
        id
        name
        slug
        purchaseProduct {
          id
          paymentIsoCode
          organisation
          content {
            reportIcon
          }
          grantedEntitlements {
            key
          }
        }
      }
      functionalDatapointCounts {
        inRangeCount
        outOfRangeCount
        untestedCount
      }
      dnaDatapointCounts {
        inRangeCount
        outOfRangeCount
        untestedCount
      }
      questionsAndAnswersCounts {
        inRangeCount
        outOfRangeCount
        untestedCount
      }
    }
  }
  ${REPORT_FIELDS}
`;

export const REPORT_DETAIL_QUERY = gql`
  query ReportDetailQuery($subsectorSlug: String!) {
    report(slug: $subsectorSlug) {
      ...ReportFields
      assessedDatapoints {
        id
        name
        isAvailable
        testProducts {
          id
          name
          paymentIsoCode
          organisation
          productFamily {
            id
            name
          }
        }
      }
      questionsAndAnswers {
        id
        question {
          id
          symptomGroup
        }
        answerScore
        answerText
      }
    }
    userReport(subsectorSlug: $subsectorSlug) {
      ...UserReportFields
      snpsForDiagram
      expectedSnps
      dnaDatapointCounts {
        inRangeCount
        outOfRangeCount
        untestedCount
      }
      functionalDatapointCounts {
        inRangeCount
        outOfRangeCount
        untestedCount
      }
      questionsAndAnswersCounts {
        inRangeCount
        outOfRangeCount
        untestedCount
      }
      dnaDatapointsOutOfRange {
        id
        riskAllele(useSnpValue: true)
        value
        impactActivity
        snpInterpretation
        snpSuggestion
        datapoint {
          id
          name
        }
      }
      functionalDatapointsOutOfRange {
        id
        value
        sampleType
        dateExtracted
        chartConfiguration {
          interpretation
        }
        datapoint {
          id
          name
          units
        }
      }
      questionsAndAnswersOutOfRange {
        id
        question {
          id
          symptomGroup
        }
        answerScore
        answerText
      }
      subsector {
        id
        name
        slug
        reportInformationPanels {
          id
          title
          body
          diagram
        }
      }
    }
  }
  ${REPORT_FIELDS}
  ${USER_REPORT_FIELDS}
`;

export const REPORT_RESULTS_QUERY = gql`
  query ReportResultsQuery($subsectorSlug: String!, $first: Int!, $after: String!) {
    userReport(subsectorSlug: $subsectorSlug) {
      id
      dnaDatapointCounts {
        inRangeCount
        outOfRangeCount
        untestedCount
      }
      functionalDatapointCounts {
        inRangeCount
        outOfRangeCount
        untestedCount
      }
      questionsAndAnswersCounts {
        inRangeCount
        outOfRangeCount
        untestedCount
      }
      userResultConnections(after: $after, first: $first) {
        edges {
          cursor
          node {
            id
            dnaDatapointCounts {
              inRangeCount
              outOfRangeCount
              untestedCount
            }
            functionalDatapointCounts {
              inRangeCount
              outOfRangeCount
              untestedCount
            }
            symptomCounts {
              inRangeCount
              outOfRangeCount
              untestedCount
            }
            result {
              id
              name
              description
              diagram
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

export const REPORT_RELATED_TESTS_QUERY = gql`
  query CustomerReportRelatedTestsQuery(
    $report: String!
    $allTests: Boolean!
    $excludeRetests: Boolean!
  ) {
    userTests(allTests: $allTests, excludeRetests: $excludeRetests, report: $report) {
      id
      name
      reviewStatus
      createdDateFormatted
      displayOrderId
    }
    ordersInProgress(report: $report) {
      ...OrderTestItemFields
      order {
        id
      }
    }
  }
  ${ORDER_TEST_ITEM_FIELDS}
`;

export const USER_RESULT_INTERPRETATION_DETAILS_QUERY = gql`
  query UserResultInterpretationDetailsQuery($userResultId: ID!) {
    userResult(userResultId: $userResultId) {
      id
      snpsForDiagram
      expectedSnps
      questionsAndAnswers {
        id
        question {
          id
          symptomGroup
        }
        answerText
        answerScore
      }
      dnaDatapoints {
        id
        riskAllele(useSnpValue: true)
        value
        interpretation
        impactActivity
        snpInterpretation
        snpSuggestion
        datapoint {
          id
          name
        }
      }
      untestedDnaDatapoints {
        id
        name
      }
      untestedDatapoints {
        id
        name
        advertisedTestProductName
        advertisedTestProductSlug
      }
      functionalDatapoints {
        id
        value
        sampleType
        dateExtracted
        chartConfiguration {
          interpretation
        }
        datapoint {
          id
          name
          units
        }
      }
    }
  }
`;
